// Content for the Communications page "Le défi" section (fragmentation — the before state).
export type Bi = { fr: string; en: string }
export type StatusTone = 'green' | 'amber' | 'red'

export const RESIDENT_PHOTO =
  'https://cdn.magicpatterns.com/patterns/generated-images/7415bfc8-3b8c-48c9-a85a-193c39b49e2f.jpg'

export const challengeIntro = {
  eyebrow: { fr: 'Le défi', en: 'The challenge' },
  titleLines: {
    fr: ['Communiquer partout ne veut pas dire', 'joindre tout le monde.'],
    en: ['Communicating everywhere doesn’t mean', 'reaching everyone.'],
  },
  body: {
    fr: [
      'Les publics sont fragmentés. Les préférences varient. Les canaux se multiplient.',
      'Et trop souvent, l’identité, le contexte, le message et les résultats restent séparés.',
    ],
    en: [
      'Audiences are fragmented. Preferences vary. Channels keep multiplying.',
      'And too often, identity, context, message and results remain separate.',
    ],
  },
}

export const personCard = {
  title: { fr: ['Une même', 'personne.'], en: ['One and the', 'same person.'] },
  relationships: [
    { key: 'citizen', label: { fr: 'Citoyenne', en: 'Resident' } },
    { key: 'owner', label: { fr: 'Propriétaire', en: 'Property owner' } },
    { key: 'family', label: { fr: 'Famille', en: 'Family' } },
    { key: 'subscriber', label: { fr: 'Abonnée', en: 'Subscriber' } },
    { key: 'leisure', label: { fr: 'Loisirs', en: 'Recreation' } },
  ],
} as const

export const challengeChannels: {
  key: 'push' | 'sms' | 'email' | 'phone' | 'web' | 'social'
  label: Bi
  status: Bi
  tone: StatusTone
}[] = [
  { key: 'push', label: { fr: 'Push', en: 'Push' }, status: { fr: 'Envoyé', en: 'Sent' }, tone: 'green' },
  { key: 'sms', label: { fr: 'SMS', en: 'SMS' }, status: { fr: 'Livré ?', en: 'Delivered?' }, tone: 'amber' },
  { key: 'email', label: { fr: 'Courriel', en: 'Email' }, status: { fr: 'Ouvert ?', en: 'Opened?' }, tone: 'amber' },
  { key: 'phone', label: { fr: 'Téléphone', en: 'Phone' }, status: { fr: 'Non joint', en: 'Not reached' }, tone: 'red' },
  { key: 'web', label: { fr: 'Web / Portail', en: 'Web / Portal' }, status: { fr: 'Doublon', en: 'Duplicate' }, tone: 'red' },
  { key: 'social', label: { fr: 'Réseaux sociaux', en: 'Social media' }, status: { fr: 'Préférence ?', en: 'Preference?' }, tone: 'amber' },
]

export const problemCards: { key: 'systems' | 'messages' | 'tracking'; title: Bi; body: Bi }[] = [
  {
    key: 'systems',
    title: { fr: 'Outils et systèmes séparés', en: 'Separate tools and systems' },
    body: { fr: 'Listes multiples, mises à jour difficiles, données incomplètes.', en: 'Multiple lists that are hard to keep up to date, incomplete data.' },
  },
  {
    key: 'messages',
    title: { fr: 'Messages non adaptés', en: 'Messages not tailored' },
    body: { fr: 'Formats, langues et canaux différents à gérer.', en: 'Different formats, languages and channels to manage.' },
  },
  {
    key: 'tracking',
    title: { fr: 'Aucun suivi unifié', en: 'No unified tracking' },
    body: { fr: 'Difficile de savoir qui a été joint et quoi faire ensuite.', en: 'Hard to know who was reached and what to do next.' },
  },
]

export const outcomes: { key: 'push' | 'email' | 'phone' | 'sms'; title: Bi; sub: Bi; ok: boolean }[] = [
  { key: 'push', title: { fr: 'Avis envoyé', en: 'Notice sent' }, sub: { fr: 'Push mobile', en: 'Mobile push' }, ok: true },
  { key: 'email', title: { fr: 'Courriel envoyé', en: 'Email sent' }, sub: { fr: 'Infolettre générale', en: 'General newsletter' }, ok: true },
  { key: 'phone', title: { fr: 'Appel automatisé', en: 'Automated call' }, sub: { fr: 'Boîte vocale', en: 'Voicemail' }, ok: false },
  { key: 'sms', title: { fr: 'SMS envoyé', en: 'SMS sent' }, sub: { fr: 'Numéro périmé', en: 'Outdated number' }, ok: false },
]

export const evidence: {
  key: 'channels' | 'citizens' | 'consumers'
  value: Bi
  label: Bi
  body: Bi
  source: string
  url: string
}[] = [
  {
    key: 'channels',
    value: { fr: '3 à 5', en: '3 to 5' },
    label: { fr: 'Canaux utilisés', en: 'Channels used' },
    body: {
      fr: 'Plus de la moitié des clients utilisent 3 à 5 canaux au cours d’un même parcours.',
      en: 'More than half of customers use 3 to 5 channels during a single journey.',
    },
    source: 'McKinsey, 2023',
    url: 'https://www.mckinsey.com/capabilities/growth-marketing-and-sales/our-insights',
  },
  {
    key: 'citizens',
    value: { fr: '36 %', en: '36%' },
    label: { fr: 'Des citoyens', en: 'Of citizens' },
    body: {
      fr: 'Seulement 36 % des personnes interrogées considèrent les processus et interactions avec les organismes gouvernementaux comme intuitifs.',
      en: 'Only 36% of respondents consider processes and interactions with government agencies to be intuitive.',
    },
    source: 'Accenture, 2023',
    url: 'https://www.accenture.com/us-en/insights/public-service',
  },
  {
    key: 'consumers',
    value: { fr: '45 %', en: '45%' },
    label: { fr: 'Des consommateurs', en: 'Of consumers' },
    body: {
      fr: 'Seulement 45 % des consommateurs se sentent compris par les organisations avec lesquelles ils interagissent.',
      en: 'Only 45% of consumers feel understood by the organizations they interact with.',
    },
    source: 'Twilio, 2025',
    url: 'https://www.twilio.com/en-us/reports',
  },
]

export const transition = {
  lead: { fr: 'Le défi n’est plus d’envoyer un message.', en: 'The challenge is no longer sending a message.' },
  main: {
    fr: 'C’est de savoir qui joindre, comment, quand — et si la personne a réellement été jointe.',
    en: 'It’s knowing who to reach, how, when — and whether the person was actually reached.',
  },
}
